import { Country } from '@ui/src';
import {
  Questionnarie,
  EmploymentStatus,
  OrganizationType,
  CustomerStatus,
  SignatureType,
  UserAccountProps,
} from '../common/types';
import { FeeItem } from '../common/tariff/types';
import { LinkedClient } from '../common/linked-clients/types';
import { NatureOfControlEnum } from '../new-application/types';

export interface BaseInfoFormValues {
  type: OrganizationType;
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  employmentStatus: string;
  clientActivityId?: string;
  idNumber?: string;
  idType?: string;
  idExpDate?: string;
  countryId: string;
  city: string;
  address1: string;
  address2: string;
  postalCode: string;
  email?: string;
  phone?: string;
  sourceOfFundsIds: string[];
  productIds: string[];
  nationalityId: string;
  taxNumber?: string;
  birthCountryId?: string;
  placeOfBirth?: string;
  nameOfEmployer?: string;
  employeePosition?: string;
  countryOfEmployerId?: string;
  correspondenceAddress1?: string;
  correspondenceAddress2?: string;
  correspondenceAddressSameAsRegisteredAddress?: boolean;
  correspondenceCity?: string;
  correspondenceCountryId?: string;
  correspondencePostalCode?: string;
  isUSResident?: string | boolean;
  typeOfActivityDescription?: string;
}

export interface BaseUserInformation {
  firstName: string;
  lastName: string;
  countryId: string;
  city: string;
  address1: string;
  address2: string;
  postalCode: string;
}

export interface GeneralInfo {
  id?: string;
  firstName: string;
  lastName: string;
  applicationId?: string;
  dateOfBirth: string;
  status: CustomerStatus;
  employmentStatus?: EmploymentStatus;
  statusChangeReason?: string;
  phone: string;
  email: string;
  totalBalance: number;
  currency: string;
  risk: number;
  accounts?: UserAccountProps[];
  currencies: string[];
  cards: string[];
  tariff: string;
  clientActivityId?: string;
  typeOfActivityDescription?: string;
  tariffId?: string;
  fees?: FeeItem[];
  tariffRiskScoreId?: string;
  defaultFees?: FeeItem[];
  type?: OrganizationType;
  nationalityId: string;
  idType?: string;
  idNumber?: string;
  idExpDate?: string;
  taxNumber?: string;
  monthlyMaxValueIncomingPaymentRangeId?: string;
  monthlyMaxValueOutgoingPaymentRangeId?: string;
  monthlyNumberIncomingTransactionsRangeId?: string;
  monthlyNumberOutgoingTransactionsRangeId?: string;
  monthlyTurnoverRangeId?: string;
  monthlyValueIncomingTransactionsRangeId?: string;
  monthlyValueOutgoingTransactionsRangeId?: string;
  userId?: string;
  questionnaire?: Questionnarie;
  totpEnabled?: boolean;
  pspIntegrationOrganization?: Partial<LinkedClient>;
  birthCountryId?: string;
  placeOfBirth?: string;
  nameOfEmployer?: string;
  employeePosition?: string;
  countryOfEmployerId?: string;
  correspondenceAddress1?: string;
  correspondenceAddress2?: string;
  correspondenceAddressSameAsRegisteredAddress?: boolean;
  correspondenceCity?: string;
  correspondenceCountry?: string;
  correspondenceCountryId?: string;
  correspondencePostalCode?: string;
  isUSResident?: boolean | string;
  clientScheme?: {
    id: string;
    name: string;
  };
  user?: {
    profile?: {
      dob: string;
    };
  };
}

export interface GeneralUserInfo {
  fullName: string;
  dateOfBirth: string;
  nationalityId: string;
  employmentStatus: string;
  idType?: string;
  idNumber?: string;
  idExpDate?: string;
  residenceCountry: string;
  residenceAddress: string;
  email: string;
  phone: string;
  appAccess: boolean;
  webAccess: boolean;
  deviceOs: string;
  appVersion: string;
  appLastLogin: string;
  webLastLogin: string;
  clientActivityId?: string;
  webOs: string;
  scaAuthenticationsType: string;
  sourceOfFunds?: string[];
  clientScheme?: {
    id: string;
  };
  products?: string[];
  type?: OrganizationType;
  taxNumber?: string;
  pspIntegrationOrganization?: Partial<LinkedClient>;
  representativeOfOrganizations?: {
    authLevel: string;
    companyOfficialType: string;
    createdAt: string;
    id: string;
    rejectedAt: string;
    archivedAt: string;
    archivedBy: string;
    rejectedBy: string;
    approvedBy: string;
    approvedAt: string;
    rejectionReason?: string;
    natureOfControl: NatureOfControlEnum;
    organizationId: string;
    signatureType: SignatureType;
    accessRight?: string;
    percentage: string;
    position: string;
    status: string;
    havingControl?: boolean;
    user?: {
      profile?: {
        firstName: string;
        lastName: string;
      };
    };
    organization: {
      companyName: string;
      id: string;
    };
  }[];
}

export interface Device {
  active: boolean;
  auditLogs: any[];
  browser: string;
  country: Country;
  createdAt: string;
  deletedAt: string;
  id: string;
  mobile: boolean;
  platform: string;
  trusted: boolean;
  updatedAt: string;
  ip?: string;
  render?: Record<string, React.JSX.Element>;
}

export interface DeviceSession {
  id: string;
  createdAt: string;
  updatedAt?: string;
  deletedAt?: string;
  auditId: string;
  accessTokenExpiresAt: string;
  deviceId: string;
  refreshTokenExpiresAt: string;
  userId: string;
  render?: Record<string, React.JSX.Element>;
}

export enum LockReasonEnum {
  USER_TOO_MANY_WRONG_PASSWORDS = 'USER_TOO_MANY_WRONG_PASSWORDS',
  USER_TOO_MANY_WRONG_2FA_CODES = 'USER_TOO_MANY_WRONG_2FA_CODES',
  USER_TOO_MANY_WRONG_PINS = 'USER_TOO_MANY_WRONG_PINS',
  USER_TOO_MANY_VERIFY_SMS = 'USER_TOO_MANY_VERIFY_SMS',
  USER_TOO_MANY_VERIFY_EMAILS = 'USER_TOO_MANY_VERIFY_EMAILS',
  USER_TOO_MANY_VERIFY_PHONE_ATTEMPTS = 'USER_TOO_MANY_VERIFY_PHONE_ATTEMPTS',
  GLOBAL_SYSTEM_DOWNTIME = 'GLOBAL_SYSTEM_DOWNTIME',
  TWILIO_THROTTLE = 'TWILIO_THROTTLE',
  MANUAL = 'MANUAL',
}

export enum LockReasonTitle {
  USER_TOO_MANY_WRONG_PASSWORDS = 'User too many wrong passowrds',
  USER_TOO_MANY_WRONG_2FA_CODES = 'User too many wrong 2FA codes',
  USER_TOO_MANY_WRONG_PINS = 'User too many wrong pins',
  USER_TOO_MANY_VERIFY_SMS = 'User too many verify sms',
  USER_TOO_MANY_VERIFY_EMAILS = 'User too many verify emails',
  USER_TOO_MANY_VERIFY_PHONE_ATTEMPTS = 'User too many verify phone attempts',
  GLOBAL_SYSTEM_DOWNTIME = 'Global system downtime',
  TWILIO_THROTTLE = 'Twilio throttle',
  MANUAL = 'Manual',
}

export enum LockAuditLogActionEnum {
  USER_AUTH_EMAIL = 'USER_AUTH_EMAIL',
  USER_AUTH_PHONE = 'USER_AUTH_PHONE',
  USER_VERIFY_TOTP = 'USER_VERIFY_TOTP',
  USER_VERIFY_CODE = 'USER_VERIFY_CODE',
  USER_VERIFY_PASSWORD = 'USER_VERIFY_PASSWORD',
  USER_AUTH_PIN = 'USER_AUTH_PIN',
  USER_SET_PIN = 'USER_SET_PIN',
  USER_SEND_RESET_PASSWORD = 'USER_SEND_RESET_PASSWORD',
  USER_VERIFY_RESET_CODE = 'USER_VERIFY_RESET_CODE',
  USER_REFRESH_TOKEN = 'USER_REFRESH_TOKEN',
  USER_CONVERT_TOKEN = 'USER_CONVERT_TOKEN',
  USER_SIGNATURE_VERIFY_CODE = 'USER_SIGNATURE_VERIFY_CODE',
  USER_GENERATE_CHECKSUM = 'USER_GENERATE_CHECKSUM',
  USER_VERIFY_SIGNATURE = 'USER_VERIFY_SIGNATURE',
  AUTH_CREDENTIAL_PHONE_SEND_SMS = 'AUTH_CREDENTIAL_PHONE_SEND_SMS',
  USER_SEND_EMAIL_CODE_SIGNATURE = 'USER_SEND_EMAIL_CODE_SIGNATURE',
  USER_SEND_PUSH_CODE_SIGNATURE = 'USER_SEND_PUSH_CODE_SIGNATURE',
  USER_SEND_SMS_CODE_SIGNATURE = 'USER_SEND_SMS_CODE_SIGNATURE',
  USER_SEND_TOTP_CODE_SIGNATURE = 'USER_SEND_TOTP_CODE_SIGNATURE',
}

export enum LockAuditLogTitle {
  USER_AUTH_EMAIL = 'User auth email',
  USER_AUTH_PHONE = 'User auth phone',
  USER_VERIFY_TOTP = 'User verify totp',
  USER_VERIFY_CODE = 'User verify code',
  USER_VERIFY_PASSWORD = 'User verify password',
  USER_AUTH_PIN = 'User auth pin',
  USER_SET_PIN = 'User set pin',
  USER_SEND_RESET_PASSWORD = 'User send reset password',
  USER_VERIFY_RESET_CODE = 'User verify reset code',
  USER_REFRESH_TOKEN = 'User refresh token',
  USER_CONVERT_TOKEN = 'User convert token',
  USER_SIGNATURE_VERIFY_CODE = 'User signature verify code',
  USER_GENERATE_CHECKSUM = 'User generate checksum',
  USER_VERIFY_SIGNATURE = 'User verify signature',
  AUTH_CREDENTIAL_PHONE_SEND_SMS = 'Auth credential phone send sms',
  USER_SEND_EMAIL_CODE_SIGNATURE = 'User send email code signature',
  USER_SEND_PUSH_CODE_SIGNATURE = 'User send push code signature',
  USER_SEND_SMS_CODE_SIGNATURE = 'User send sms code signature',
  USER_SEND_TOTP_CODE_SIGNATURE = 'User send totp code signature',
}

export enum LockAuditLogResultEnum {
  SUCCESSFUL = 'SUCCESSFUL',
  FAILED = 'FAILED',
}

export interface Lock {
  id: string;
  locked: boolean;
  reason: LockReasonEnum;
  userId?: string;
  createdByEmployeeId?: string;
  expiresAt?: string;
  createdByComment?: string;
  createdAt: string;
  type: string;
  render?: Record<string, React.JSX.Element>;
}

export interface LockFormValues {
  reason: string;
  userId?: string;
  locked?: boolean;
  expiresAt?: string;
  createdByComment?: string;
}

export interface LockAuditLog {
  id: string;
  action: LockAuditLogActionEnum;
  ipSource?: string;
  userId?: string;
  result: LockAuditLogResultEnum;
  lockId?: string;
  deviceId?: string;
  audience?: string;
  nextAction?: string;
  createdAt?: string;
  eventCreatedAt?: string;
  render?: Record<string, React.JSX.Element>;
}

export enum IdType {
  ID_CARD = 'ID_CARD',
  PASSPORT = 'PASSPORT',
  DRIVERS = 'DRIVERS',
  RESIDENCE_PERMIT = 'RESIDENCE_PERMIT',
  UTILITY_BILL = 'UTILITY_BILL',
  SELFIE = 'SELFIE',
  VIDEO_SELFIE = 'VIDEO_SELFIE',
  PROFILE_IMAGE = 'PROFILE_IMAGE',
  ID_DOC_PHOTO = 'ID_DOC_PHOTO',
  AGREEMENT = 'AGREEMENT',
  CONTRACT = 'CONTRACT',
  DRIVERS_TRANSLATION = 'DRIVERS_TRANSLATION',
  INVESTOR_DOC = 'INVESTOR_DOC',
  VEHICLE_REGISTRATION_CERTIFICATE = 'VEHICLE_REGISTRATION_CERTIFICATE',
  INCOME_SOURCE = 'INCOME_SOURCE',
  PAYMENT_METHOD = 'PAYMENT_METHOD',
  BANK_CARD = 'BANK_CARD',
  COVID_VACCINATION_FORM = 'COVID_VACCINATION_FORM',
  OTHER = 'OTHER',
}

export interface COFAndIndividualQuestionnarieFormValues {
  nationalInsuranceNumberOfDirectors?: string;
  lastThreeYearsAddressHistory?: string;
  criminalConvictedOfAny?: string | boolean;
  criminalReceivedACautionForAny?: string | boolean;
  criminalArrestedOrChargedWithOrBeenInvolved?: string | boolean;
  criminalSubjectToAnyCurrentCriminalInvestigation?: string | boolean;
  criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted?:
    string | boolean;
  criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered?:
    string | boolean;
  criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved?:
    string | boolean;
  civilFoundLiableForFraud?: string | boolean;
  civilSubjectToAJudgmentDebt?: string | boolean;
  civilInvolvedInAnyCivilProceedings?: string | boolean;
  civilYouHeldAPositionOfInfluence?: string | boolean;
  civilFiledForBankruptcy?: string | boolean;
  civilSubjectToABankruptcy?: string | boolean;
  businessAndEmploymentDismissedOrAskedTo?: string | boolean;
  businessAndEmploymentDisqualifiedFromActingAsADirector?: string | boolean;
  businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings?:
    string | boolean;
  regulatoryAnAuthorizationFromARegulatoryBodyRefused?: string | boolean;
  regulatoryCensured?: string | boolean;
  regulatoryInvolvedInAnyActivitiesRequiringAuthorization?: string | boolean;
  regulatorySubjectToAnyDisqualification?: string | boolean;
  otherNationalitiesIds?: string[];
  maritalStatus?: string;
  spouseFullName?: string;
  natureOfBusinessActivity?: string;
  nameOfCompany?: string;
  ownership?: number | string;
  belongsToGroup?: boolean | string;
  parentCompanyName?: string;
  parentCompanyCountryId?: string;
  subsidiariesInfo?: string;
  mainActivities?: string;
  groupWealthSourceIds?: string[];
  estimatedTotalWealthEuro?: string | number;
  requiresSourcesOfIncomes?: string | boolean;
  sourcesOfIncomes?:
    | {
        id?: string;
        sourceId: string;
        value: string | number;
      }[]
    | null;
  sourcesOfIncomesOther?: string;
  sourcesOfIncomesBorrowedOther?: string;
  requiresSourceAndSizeOfWealth?: string | boolean;
  sourcesAndSizesOfWealth?:
    | {
        id?: string;
        sourceId: string;
        value: string | number;
      }[]
    | null;
  sourcesAndSizesOfWealthOther?: string;
  sourcesAndSizesOfWealthBorrowedOther?: string;
  requiresNetWorth?: string | boolean;
  netWorthAmount?: string | number | null;
  questionnairesAccountsCurrencies?: string;
  questionnairesAccountsFlows?: string;
  questionnairesExpectedFundsOrigins?: string;
  questionnairesAffiliatedCompanies?: string;
  sourcesOfFundsIds?: string[];
  sourcesOfFundsOther?: string;
  sourcesOfFundsLoanCreditInstitution?: string;
  sourcesOfFundsLoanBusinessPartner?: string;
  hasAccountsInOtherFinancialInstitutions?: string | boolean;
  otherFinancialInstitutions?: string;
  groupWealthSourceOther?: string;
  secondPassportExpiryDate?: string;
  secondPassportIssueCountryId?: string;
  secondPassportIssueDate?: string;
  secondPassportNumber?: string;
  isPepOrAssociate?: string | boolean;
  nameOfPep?: string;
  relToPep?: string;
  orgEmployerAndPosition?: string;
  positionHeldCountryId?: string;
  politicalFunctionPeriodFrom?: string;
  politicalFunctionPeriodTo?: string;
  abilityMoveGovernmentFunds?: boolean | string;
  abilityMoveGovernmentFundsDetails?: string;
  isUSCitizen?: string | boolean;
  questionnairesCrsFatcaDeclarations?: string;
  homePhoneNumber?: string;
  emails?: string[];
  accountsCurrencies?: string;
  accountsFlows?: string;
  hasAffiliatedCompanies?: string | boolean;
}
